import { isSurveyAgentTemplate } from "@wetalk/view-model/survey-agent-template";

import { portalText } from "../../../i18n/portal.js";
/**
 * The nine-tab strip of `data-screen-label="Agent header"`.
 *
 * All nine are listed because all nine are part of the screen. `delivered` says which of them
 * has a body: Live and History from P10-03, Channels from P11-04, and Analytics, Versions and
 * Review from P11-01/02/03. The rest render a deferred state that names the unit that will
 * build them. Every one of those states is registered against the `Agent` row in
 * `docs/design/screen-manifest.json` — `tools/screen-coverage` fails CI on an unregistered
 * gap, and a tab that quietly rendered nothing would be exactly that gap.
 *
 * `Orders` is conditional in the design: it appears only for templates whose outcome is an
 * order. That condition is the template's, not the tab's, so it is expressed as a predicate
 * over the template code rather than as a second list.
 *
 * AMENDED 2026-10-03 (survey results, design §16.7.2 AS BUILT 2026-10-03): a tenth tab, `Results`,
 * shown only for a survey VoiceAgent — the same kind of template condition as `Orders`. Its body is the
 * Survey results screen's own (`../../campaigns/survey-result-body.tsx`) over every conversation of the
 * survey, so the prototype's `Survey results` design is the one drawn; no second look.
 *
 * AMENDED 2026-10-09 (design sync, the Claude Design export of 2026-10-09): the strip draws SEVEN
 * groups, not ten tabs — Live, Campaigns (a survey only), Orders (an order template only),
 * Conversations (History · Review), Insights (Analytics, and Results for a survey), Test and Settings
 * (Channels · Versions). A group with more than one tab shows its tabs as a second row under the
 * strip. The tab ids — and so every link and the `tab` search parameter — are unchanged; a group is
 * only how the strip draws them. Outbound is now a survey agent's only (`Campaigns`), as the design's
 * `GROUPS` filter has it.
 *
 * AMENDED 2026-10-28 (plan-030 WAPP-Q, design §41.10): an eleventh tab, `Questions`, for a survey VoiceAgent only — the
 * survey question editor (`./questions/questions-tab.tsx`), after `results` in the ids and drawn as its own group after
 * Campaigns, because a survey's questions are what it is. It needs no live version: a first build not yet published is
 * edited too (design §41.4). Its words are the editor's own catalog (`surveyQuestionnaire`).
 */
import type { PhosphorIcon } from "@wetalk/ui";

import { GLYPH } from "../../../shell/glyph.js";

export const VOICE_AGENT_TAB_ID = [
  "live",
  "orders",
  "review",
  "outbound",
  "results",
  "questions",
  "history",
  "analytics",
  "test",
  "channels",
  "versions",
] as const;

export type VoiceAgentTabId = (typeof VOICE_AGENT_TAB_ID)[number];

export interface VoiceAgentTabDefinition {
  readonly id: VoiceAgentTabId;
  readonly label: string;
  readonly glyph: PhosphorIcon;
  /** `true` for the two P10-03 delivers; the rest render the deferred state. */
  readonly delivered: boolean;
  /** The unit that will build it, shown to the customer as the reason. */
  readonly phase: string;
  /** What the tab will hold, in the customer's words. */
  readonly promise: string;
  /** Only shown when the template's outcome is a `customer_order`. */
  readonly customerOrderOnly: boolean;
  /** Only shown for a survey VoiceAgent (`isSurveyAgentTemplate`). */
  readonly surveyOnly: boolean;
}

export const VOICE_AGENT_TAB: readonly VoiceAgentTabDefinition[] = [
  {
    id: "live",
    get label() { return portalText("operations", "live_65c821a5"); },
    glyph: GLYPH.broadcast,
    delivered: true,
    phase: "P10-03",
    get promise() { return portalText("operations", "conversations_happening_right_now_with_the_transcript_as_i_77110c3b"); },
    customerOrderOnly: false,
    surveyOnly: false,
  },
  {
    id: "orders",
    get label() { return portalText("operations", "orders_cded0933"); },
    glyph: GLYPH.receipt,
    delivered: true,
    phase: "P12-03",
    get promise() { return portalText("operations", "every_confirmed_order_with_its_caller_items_payment_and_th_3c7ba02f"); },
    customerOrderOnly: true,
    surveyOnly: false,
  },
  {
    id: "review",
    get label() { return portalText("operations", "review_e29a79fe"); },
    glyph: GLYPH.question,
    delivered: true,
    phase: "P11-03",
    get promise() { return portalText("operations", "gaps_questions_your_agent_could_not_answer_grouped_and_con_ed5621f4"); },
    customerOrderOnly: false,
    surveyOnly: false,
  },
  {
    id: "outbound",
    get label() { return portalText("operations", "campaigns_01a23a28"); },
    glyph: GLYPH.phoneOutgoing,
    delivered: true,
    phase: "P14-02",
    get promise() { return portalText("operations", "campaigns_who_to_call_when_it_is_allowed_to_call_them_and_0bad03ef"); },
    customerOrderOnly: false,
    surveyOnly: true,
  },
  {
    id: "results",
    get label() { return portalText("operations", "results_612e12d2"); },
    glyph: GLYPH.listChecks,
    delivered: true,
    get phase() { return portalText("operations", "survey_results_2026_10_03_0dd876b0"); },
    get promise() { return portalText("operations", "survey_download.results_tab_promise"); },
    customerOrderOnly: false,
    surveyOnly: true,
  },
  {
    id: "questions",
    get label() { return portalText("surveyQuestionnaire", "head.title"); },
    glyph: GLYPH.clipboardText,
    delivered: true,
    phase: "plan-030",
    get promise() { return portalText("surveyQuestionnaire", "tab.promise"); },
    customerOrderOnly: false,
    surveyOnly: true,
  },
  {
    id: "history",
    get label() { return portalText("operations", "history_90ccd649"); },
    glyph: GLYPH.clockCounterClockwise,
    delivered: true,
    phase: "P10-03",
    get promise() { return portalText("operations", "every_finished_conversation_with_audio_transcript_and_what_00ef0037"); },
    customerOrderOnly: false,
    surveyOnly: false,
  },
  {
    id: "analytics",
    get label() { return portalText("operations", "analytics_25bc9629"); },
    glyph: GLYPH.chartLineUp,
    delivered: true,
    phase: "P11-01",
    get promise() { return portalText("operations", "conversations_per_day_outcomes_language_shares_busiest_hou_735aff97"); },
    customerOrderOnly: false,
    surveyOnly: false,
  },
  {
    id: "test",
    get label() { return portalText("operations", "test_640ab2ba"); },
    glyph: GLYPH.flask,
    delivered: true,
    phase: "P11-09",
    get promise() { return portalText("operations", "test_console.place_a_test_call_or_open_a_test_chat"); },
    customerOrderOnly: false,
    surveyOnly: false,
  },
  {
    id: "channels",
    get label() { return portalText("operations", "channels_18e03e2a"); },
    glyph: GLYPH.plugsConnected,
    delivered: true,
    phase: "P11-04",
    get promise() { return portalText("operations", "which_endpoints_reach_this_agent_the_sip_credentials_the_s_94a8e83f"); },
    customerOrderOnly: false,
    surveyOnly: false,
  },
  {
    id: "versions",
    get label() { return portalText("operations", "versions_a239107e"); },
    glyph: GLYPH.gitBranch,
    delivered: true,
    phase: "P11-02",
    get promise() { return portalText("operations", "every_published_agent_version_what_changed_how_its_test_se_bf63b409"); },
    customerOrderOnly: false,
    surveyOnly: false,
  },
];

export function isVoiceAgentTabId(value: string): value is VoiceAgentTabId {
  return (VOICE_AGENT_TAB_ID as readonly string[]).includes(value);
}

/**
 * A survey agent_template: `survey`, or a variant of it (`…_survey`), as New campaign reads it. Moved to
 * `@wetalk/view-model/survey-agent-template` (plan-034 VIEW-PKG); re-exported here so no portal importer changes.
 */
export { isSurveyAgentTemplate };

/**
 * The strip for one template: `Orders` only when the template's outcome is an order, `Results` only
 * for a survey.
 */
export function tabsFor(agentTemplateCode: string): readonly VoiceAgentTabDefinition[] {
  return VOICE_AGENT_TAB.filter(
    (one) =>
      (!one.customerOrderOnly || agentTemplateCode === "delivery") &&
      (!one.surveyOnly || isSurveyAgentTemplate(agentTemplateCode)),
  );
}

/** The seven groups of the strip (design sync 2026-10-09). */
export const VOICE_AGENT_TAB_GROUP_ID = [
  "live",
  "campaigns",
  "questions",
  "orders",
  "conversations",
  "insights",
  "test",
  "settings",
] as const;

export type VoiceAgentTabGroupId = (typeof VOICE_AGENT_TAB_GROUP_ID)[number];

export interface VoiceAgentTabGroupDefinition {
  readonly id: VoiceAgentTabGroupId;
  readonly label: string;
  readonly glyph: PhosphorIcon;
  /** The tabs it holds, in the order its second row draws them; the first one opens on a click. */
  readonly tab: readonly VoiceAgentTabDefinition[];
}

const GROUP_LAYOUT: readonly {
  readonly id: VoiceAgentTabGroupId;
  readonly label: string;
  readonly glyph: PhosphorIcon;
  readonly tab: readonly VoiceAgentTabId[];
}[] = [
  { id: "live", get label() { return portalText("operations", "live_65c821a5"); }, glyph: GLYPH.broadcast, tab: ["live"] },
  { id: "campaigns", get label() { return portalText("operations", "campaigns_01a23a28"); }, glyph: GLYPH.megaphone, tab: ["outbound"] },
  { id: "questions", get label() { return portalText("surveyQuestionnaire", "head.title"); }, glyph: GLYPH.clipboardText, tab: ["questions"] },
  { id: "orders", get label() { return portalText("operations", "orders_cded0933"); }, glyph: GLYPH.receipt, tab: ["orders"] },
  { id: "conversations", get label() { return portalText("operations", "conversations_07c59b44"); }, glyph: GLYPH.chatsCircle, tab: ["history", "review"] },
  { id: "insights", get label() { return portalText("operations", "insights_b4510362"); }, glyph: GLYPH.chartLineUp, tab: ["analytics", "results"] },
  { id: "test", get label() { return portalText("operations", "test_640ab2ba"); }, glyph: GLYPH.flask, tab: ["test"] },
  { id: "settings", get label() { return portalText("operations", "settings_c7f73bb5"); }, glyph: GLYPH.slidersHorizontal, tab: ["channels", "versions"] },
];

/**
 * The tab an address names (2026-10-14, live bug): a tab's own id, or a section's — `?tab=settings` opens Settings'
 * first tab (Channels) as a click on Settings does, where it used to fall back to Live. `null` for anything else.
 */
export function voiceAgentTabOfAddress(value: string): VoiceAgentTabId | null {
  if (isVoiceAgentTabId(value)) return value;
  return GROUP_LAYOUT.find((group) => group.id === value)?.tab[0] ?? null;
}

/**
 * The groups over the tabs one agent offers (`tabsFor`, less any a missing live version leaves out):
 * each group keeps only its offered tabs, and a group with none is not drawn.
 */
export function tabGroupsOf(offered: readonly VoiceAgentTabDefinition[]): readonly VoiceAgentTabGroupDefinition[] {
  return GROUP_LAYOUT.map((group) => ({
    id: group.id,
    label: group.label,
    glyph: group.glyph,
    tab: group.tab.flatMap((id) => offered.filter((one) => one.id === id)),
  })).filter((group) => group.tab.length > 0);
}
